import { useState } from "react";
import { XLogo, FacebookLogo, LinkSimple, ShareNetwork, Check } from "@phosphor-icons/react";
import { toast } from "@/components/ui/sonner";

// One-tap sharing for a single title. D-pad/TV friendly (focusable pills + amber focus ring).
async function writeClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function ShareRow({ title, videoId, url: shareUrl, text: shareText }) {
  const [copied, setCopied] = useState(false);
  // Point shares at the server-rendered OG endpoint: crawlers (FB/WhatsApp/X) read the
  // branded card + meta tags, humans are instantly redirected into the SPA /watch page.
  // Works on managed hosting with no Nginx crawler-routing rule required.
  const url = shareUrl || `${window.location.origin}/api/og/${videoId}`;
  const text = shareText || `Watch ${title} for FREE on ReelVault!`;
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const copyLink = async () => {
    const ok = await writeClipboard(url);
    if (ok) {
      setCopied(true);
      toast.success("Link copied!", { description: "Ready to share." });
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Could not copy link", { description: "Copy it manually from the address bar." });
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title: `${title} · ReelVault`, text, url });
    } catch {
      /* user dismissed the share sheet */
    }
  };

  const pill =
    "inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.12em] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-amber";

  return (
    <div data-testid="share-row" className="mt-6 flex flex-wrap items-center gap-3">
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/30 mr-1">Share</span>

      <a
        href={fbUrl}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={0}
        data-testid="share-facebook"
        className={`${pill} border-[#1877F2] bg-[#1877F2] text-white hover:bg-[#0f63d6] hover:border-[#0f63d6]`}
      >
        <FacebookLogo size={15} weight="fill" /> Share on Facebook
      </a>

      <a
        href={xUrl}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={0}
        data-testid="share-x"
        className={`${pill} border-white/15 text-white/70 hover:border-white hover:text-white`}
      >
        <XLogo size={15} weight="fill" /> X
      </a>


      <button
        onClick={copyLink}
        tabIndex={0}
        data-testid="share-copy"
        className={`${pill} ${copied ? "border-amber text-amber" : "border-white/15 text-white/70 hover:border-amber hover:text-amber"}`}
      >
        {copied ? <Check size={15} weight="bold" /> : <LinkSimple size={15} weight="bold" />}
        {copied ? "Copied" : "Copy Link"}
      </button>

      {canNativeShare && (
        <button
          onClick={nativeShare}
          tabIndex={0}
          data-testid="share-native"
          className={`${pill} border-white/15 text-white/70 hover:border-amber hover:text-amber`}
        >
          <ShareNetwork size={15} weight="fill" /> Share
        </button>
      )}
    </div>
  );
}
